/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ListTitle } from "@octopusdeploy/design-system-components";
import type { TeamResource, ResourceCollection, ScopedUserRoleResource, SpaceResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type React from "react";
import { useState } from "react";
import { SpaceChip, spaceChipList } from "~/components/Chips";
import buildValueList from "~/components/EventFilter/buildValueList";
import { PagingList } from "~/components/PagingList/PagingList";
import styles from "./style.module.less";
interface TeamListProps {
    initialTeams: ResourceCollection<TeamResource>;
    initialScopedUserRolesLookup: Record<string, ScopedUserRoleResource[]>;
    spaces: SpaceResource[];
    additionalRequestParams?: Map<string, {}>;
    loadScopedUserRolesForTeams(teams: TeamResource[]): Promise<Record<string, ScopedUserRoleResource[]>>;
}
const redirectUrl = (team: TeamResource) => {
    return links.teamPage.generateUrl({ teamId: team.Id });
};
const TeamList: React.FC<TeamListProps> = (props) => {
    const [scopedUserRolesLookup, setScopedUserRolesLookup] = useState(props.initialScopedUserRolesLookup);
    const loadMoreScopedUserRoles = async (team: TeamResource[]) => {
        const scopedUserRolesForNewTeams = await props.loadScopedUserRolesForTeams(team);
        setScopedUserRolesLookup((prev) => ({ ...prev, ...scopedUserRolesForNewTeams }));
    };
    return (<div className={styles.list}>
            <PagingList<TeamResource> accessibleName="Teams list" initialData={props.initialTeams} onRow={(team: TeamResource) => buildRow(team, scopedUserRolesLookup, props.spaces)} filterSearchEnabled={false} additionalRequestParams={props.additionalRequestParams} onRowRedirectUrl={redirectUrl} onFilter={() => true} showPagingInNumberedStyle={true} onNewItems={async (teams: TeamResource[]) => {
            await loadMoreScopedUserRoles(teams);
            return teams;
        }}/>
        </div>);
};
TeamList.displayName = "TeamList"
const buildRow = (team: TeamResource, scopedUserRoleLookup: Record<string, ScopedUserRoleResource[]>, spaces: SpaceResource[]) => {
    const membersLength = team.MemberUserIds.length;
    const externalGroupLength = team.ExternalSecurityGroups.length;
    const scopedUserRoles = scopedUserRoleLookup[team.Id];
    return (<div>
            {buildTeamTitle(team, spaces)}
            <div>
                This team has{" "}
                <strong>
                    {membersLength} member{membersLength === 1 ? "" : "s"}
                </strong>
                {buildExternalGroupsSpan(externalGroupLength)}
                {buildPermissionsSpan(scopedUserRoles, team, spaces)}
            </div>
        </div>);
};
const buildTeamTitle = (team: TeamResource, spaces: SpaceResource[]) => {
    const teamSpace: SpaceResource = spaces.find((s) => team.SpaceId === s.Id) || null!;
    return (<ListTitle>
            {team.Name}
            {teamSpace && <SpaceChip space={teamSpace} description={`This team belongs to the ${teamSpace.Name} space`}/>}
        </ListTitle>);
};
const buildPermissionsSpan = (scopedUserRoles: ScopedUserRoleResource[], team: TeamResource, spaces: SpaceResource[]) => {
    if (scopedUserRoles && scopedUserRoles.length > 0) {
        const elements: JSX.Element[] = [];
        const hasUnspacedPermissions = scopedUserRoles.filter((x) => x.SpaceId === null).length > 0;
        const hasSpacedPermissions = scopedUserRoles.filter((x) => x.SpaceId !== null).length > 0;
        if (hasUnspacedPermissions) {
            elements.push(<span key="systempermissions">
                    &nbsp;and <strong>system permissions</strong>
                </span>);
        }
        if (hasSpacedPermissions) {
            const validSpaceIds = scopedUserRoles.filter((x) => x.SpaceId !== null).map((x) => x.SpaceId!);
            const spaceChips = spaceChipList(spaces, validSpaceIds);
            const spaceChipsElement = <span key={`${team.Id}-spaces`}>{buildValueList(spaceChips)}</span>;
            elements.push(<span key="permissions">
                    &nbsp;and permissions for the {spaceChipsElement} space{spaceChips.length > 1 ? "s" : ""}
                </span>);
        }
        return elements;
    }
    return null;
};
const buildExternalGroupsSpan = (externalGroupLength: number) => {
    if (externalGroupLength > 0) {
        return (<span key="externalGroups">
                &nbsp;and{" "}
                <strong>
                    {externalGroupLength} external group{externalGroupLength === 1 ? "" : "s"}
                </strong>
            </span>);
    }
    return null;
};
export default TeamList;
